import React, { useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@material-ui/core";
import {
  Edit as EditIcon,
  Add as AddIcon,
  CallMerge as MergeIcon,
} from "@material-ui/icons";
import { useTranslation } from "../../hooks/useTranslation";
import SpeakerBadge from "./SpeakerBadge";
import SpeakerEditDialog from "./SpeakerEditDialog";
import SpeakersMergeDialog from "./SpeakersMergeDialog";
import {
  addSpeaker,
  updateSpeaker,
  mergeSpeakers,
  countSentencesForSpeaker,
} from "../../utils/speakerHelpers";

/**
 * Barra com a lista de speakers identificados na transcrição (área 3 do
 * painel). Em modo "REVISAO" + não aprovado, exibe ícones para editar cada
 * speaker, adicionar novos e mesclar dois ou mais em um.
 *
 * Em modo somente leitura ou aprovado, apresenta apenas os badges (sem ações).
 *
 * Props:
 *   - `transcription`  conteúdo `{speakers, sentences}` atual
 *   - `editable`       boolean — habilita ações de edição
 *   - `onChange(nextTranscription)` callback chamado com o conteúdo modificado
 */
function SpeakersBar({ transcription, editable, onChange }) {
  const { t } = useTranslation("common");
  const speakers = transcription?.speakers || [];
  const sentences = transcription?.sentences || [];

  const [editTarget, setEditTarget] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  const handleEdit = (speaker) => {
    setEditTarget(speaker);
  };

  const handleEditConfirm = ({ name, role }) => {
    if (!editTarget) return;
    onChange?.(updateSpeaker(transcription, editTarget.speaker_id, { name, role }));
    setEditTarget(null);
  };

  const handleCreateConfirm = ({ name, role }) => {
    const { transcription: next } = addSpeaker(transcription, { name, role });
    onChange?.(next);
    setCreateOpen(false);
  };

  const handleMergeConfirm = ({ mergeIds, targetId, name, role }) => {
    onChange?.(
      mergeSpeakers(transcription, mergeIds, targetId, { name, role }),
    );
    setMergeOpen(false);
  };

  const canMerge = speakers.length >= 2;

  return (
    <Box className="transcription-speakers-bar">
      <Box className="transcription-speakers-bar__header">
        <Typography
          variant="subtitle2"
          className="transcription-speakers-bar__title"
        >
          {t("transcription.speakers.title", { count: speakers.length })}
        </Typography>
        {editable && (
          <Box className="transcription-speakers-bar__actions">
            <Tooltip title={t("transcription.speakers.add")}>
              <span>
                <IconButton
                  size="small"
                  className="transcription-speakers-bar__action-btn"
                  onClick={() => setCreateOpen(true)}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                canMerge
                  ? t("transcription.speakers.merge")
                  : t("transcription.speakers.mergeDisabled")
              }
            >
              <span>
                <IconButton
                  size="small"
                  className="transcription-speakers-bar__action-btn"
                  disabled={!canMerge}
                  onClick={() => setMergeOpen(true)}
                >
                  <MergeIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>

      {speakers.length === 0 ? (
        <Typography
          variant="body2"
          className="transcription-speakers-bar__empty"
        >
          {t("transcription.speakers.empty")}
        </Typography>
      ) : (
        <Box className="transcription-speakers-bar__list">
          {speakers.map((sp) => (
            <SpeakerBadge
              key={sp.speaker_id}
              speaker={sp}
              speakers={speakers}
              variant="compact"
              sentenceCount={countSentencesForSpeaker(
                sp.speaker_id,
                sentences,
              )}
              endAdornment={
                editable ? (
                  <Tooltip title={t("transcription.speakers.editTooltip")}>
                    <IconButton
                      size="small"
                      className="transcription-speakers-bar__edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(sp);
                      }}
                    >
                      <EditIcon style={{ fontSize: "0.8rem" }} />
                    </IconButton>
                  </Tooltip>
                ) : null
              }
            />
          ))}
        </Box>
      )}

      <SpeakerEditDialog
        open={Boolean(editTarget)}
        mode="edit"
        initialName={editTarget?.name || ""}
        initialRole={editTarget?.role || ""}
        onClose={() => setEditTarget(null)}
        onConfirm={handleEditConfirm}
      />
      <SpeakerEditDialog
        open={createOpen}
        mode="create"
        initialName=""
        initialRole=""
        onClose={() => setCreateOpen(false)}
        onConfirm={handleCreateConfirm}
      />
      <SpeakersMergeDialog
        open={mergeOpen}
        transcription={transcription}
        onClose={() => setMergeOpen(false)}
        onConfirm={handleMergeConfirm}
      />
    </Box>
  );
}

/* `React.memo`: a barra de speakers só precisa re-renderizar quando o
   `transcription` (lista de speakers) muda ou as props de controle dos
   diálogos mudam. Sem isso, ela re-renderizava sempre que o painel
   re-renderizasse — o que acontecia a cada nova frase ativa do player. */
export default React.memo(SpeakersBar);
